import { useIntl } from 'react-intl';
import { useEffect, useMemo, useState } from 'react';

import { conditionalHooksFactory } from '@atlaskit/platform-feature-flags-react/conditional-hooks-factory';

import type { ForgeUIExtensionQueryOptions } from '@atlassian/forge-ui/provider/ForgeUIExtensionPointProvider';
import type { UsePreFilteredExtensionsQueryOptions } from '@atlassian/forge-ui/web-client/useExtensionList';
import {
	usePrefilteredExtensionList as usePrefilteredExtensionListApollo,
	usePrefilteredExtensionListRelay,
} from '@atlassian/forge-ui/web-client/usePrefilteredExtensionList';
import type { Extension } from '@atlassian/forge-ui/web-client/graphql/types';

import { fg } from '@confluence/feature-gating';
import { usePageContentId, usePageSpaceKey } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useSpaceDetail } from '@confluence/space-utils/entry-points/useSpaceDetail.ts';
import { isTooManyRequestsError } from '@confluence/error-boundary/entry-points/isTooManyRequestsError.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';

import { FORGE_MODULE_SPACE_PAGE } from '../ForgeModuleType.ts';

import { getUsePrefilteredExtensionListOptions } from './getUsePrefilteredExtensionListOptions.ts';

export type UseExtensionListProps = {
	moduleType: string;
	queryOptions?: UsePreFilteredExtensionsQueryOptions | ForgeUIExtensionQueryOptions;
	returnBlockedExtensions?: boolean;
	skipQuery?: boolean;
	spaceContextOnly?: boolean;
	attachmentId?: string;
	contentId?: string;
	spaceKey?: string;
};

export const useExtensionList = ({
	moduleType,
	queryOptions,
	returnBlockedExtensions = false,
	skipQuery = false,
	spaceContextOnly,
	attachmentId,
	contentId: providedContentId,
	spaceKey: providedSpaceKey,
}: UseExtensionListProps): {
	extensions: Extension[];
	loading: boolean;
	error: Error | undefined;
} => {
	const { locale } = useIntl();
	const { cloudId } = useSessionData();
	const [pageSpaceKey] = usePageSpaceKey();
	const spaceKey = providedSpaceKey || pageSpaceKey;
	const [pageContentId] = usePageContentId();
	const contentId = providedContentId || pageContentId;
	const resolvedAttachmentId = fg('forge_attachment_display_conditions') ? attachmentId : undefined;
	const { error: spaceError } = useSpaceDetail(spaceKey);
	const [ssrExtensionResult] = useState(() => window.__SSR_FORGE_EXTENSIONS__?.[moduleType]);

	const options = useMemo(
		() =>
			getUsePrefilteredExtensionListOptions({
				cloudId,
				locale,
				moduleType,
				queryOptions: {
					...queryOptions,
					skip: skipQuery,
				},
				includeHidden: returnBlockedExtensions,
				spaceKey,
				contentId,
				...(resolvedAttachmentId ? { attachmentId: resolvedAttachmentId } : {}),
				spaceContextOnly,
			}),
		[
			cloudId,
			locale,
			moduleType,
			queryOptions,
			returnBlockedExtensions,
			skipQuery,
			spaceKey,
			contentId,
			resolvedAttachmentId,
			spaceContextOnly,
		],
	);

	const {
		extensions: extsData,
		loading: extsLoading,
		error: extsError,
	} = usePrefilteredExtensionList(options);

	useEffect(() => {
		// suppress 403 errors as we can't do much with them
		if (extsError && isUnauthorizedError(extsError)) {
			markErrorAsHandled(extsError);
		}

		// suppress 429 errors for offending users
		if (isTooManyRequestsError(extsError)) {
			markErrorAsHandled(extsError);
		}

		if (spaceError) {
			markErrorAsHandled(spaceError);
		}
	}, [extsError, spaceError]);

	if (
		moduleType === FORGE_MODULE_SPACE_PAGE &&
		!skipQuery &&
		extsLoading &&
		ssrExtensionResult !== undefined &&
		ssrExtensionResult.context !== undefined &&
		ssrExtensionResult.context.spaceKey === spaceKey &&
		ssrExtensionResult.context.includeHidden === returnBlockedExtensions &&
		ssrExtensionResult.context.spaceContextOnly === Boolean(spaceContextOnly) &&
		(spaceContextOnly || ssrExtensionResult.context.contentId === contentId) &&
		fg('confluence_space_apps_ssr_preload')
	) {
		return {
			extensions: ssrExtensionResult.extensions,
			loading: false,
			error: ssrExtensionResult.error,
		};
	}

	return {
		extensions: extsData,
		loading: extsLoading,
		error: extsError,
	};
};

const usePrefilteredExtensionList = conditionalHooksFactory(
	() => fg('forge-ui-migrate-extension-list-query-to-relay'),
	usePrefilteredExtensionListRelay,
	usePrefilteredExtensionListApollo,
);
